// LoginScreen — first surface a user sees on launch when no session is
// stored. Mirrors the brand bar of the shell (ink + gold) so it doesn't
// look like a different app once the user signs in. The brand markup is
// inlined in Tailwind (NOT BrandBar — its sync-dot + logout menu are
// post-auth); the old mockup brand class is dropped here.
//
// Designed for the 380px taskpane width. Submit on Enter; the "Continuer
// avec Google" button is a placeholder ("Bientôt disponible") until PR
// post-MVP wires the OAuth flow. Tailwind logical-RTL utilities only
// (CLAUDE.md §9.2) — flips for free under dir=rtl.

import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from './useAuth'
import { LanguageToggle } from '@/ui/components/LanguageToggle'

export function LoginScreen(): JSX.Element {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const isSubmitting = useAuthStore((s) => s.isSubmitting)
  const errorMessage = useAuthStore((s) => s.errorMessage)
  const login = useAuthStore((s) => s.login)

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    try {
      await login({ email, password })
    } catch {
      // useAuthStore already wrote `errorMessage` — nothing to do here.
    }
  }

  return (
    <div
      className="flex min-h-screen w-[380px] flex-col bg-white"
      data-testid="login-screen"
    >
      <div className="flex items-center gap-2 bg-ink px-3 py-2 text-white">
        <div className="font-serif text-lg leading-none">
          𝓅<span className="text-gold">.</span>
        </div>
        <div className="text-sm font-semibold tracking-tight">
          the<em className="not-italic text-gold">plaidoyer</em>
        </div>
        <div className="ms-auto">
          <LanguageToggle />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 px-6 pb-4 pt-6">
        <div>
          <h1 className="m-0 font-serif text-[18px] font-semibold leading-snug text-ink">
            {t('common.login.title')}
          </h1>
          <p className="mt-1.5 text-xs leading-relaxed text-ink/60">
            {t('common.login.subtitle')}
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-2.5">
          <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink/80">
            {t('common.login.email')}
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              data-testid="login-email"
              className="rounded-md border border-ink/20 bg-white px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-ink"
            />
          </label>

          <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink/80">
            {t('common.login.password')}
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              data-testid="login-password"
              className="rounded-md border border-ink/20 bg-white px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-ink"
            />
          </label>

          {errorMessage && (
            <div
              role="alert"
              data-testid="login-error"
              className="rounded-md border border-[var(--del)]/20 bg-[var(--del-bg)] px-2 py-1.5 text-[11.5px] leading-snug text-[var(--del)]"
            >
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            data-testid="login-submit"
            className="mt-1 rounded-md bg-ink px-3.5 py-2 text-xs font-bold text-white disabled:cursor-wait disabled:opacity-70"
          >
            {isSubmitting
              ? t('common.login.submitting')
              : t('common.login.submit')}
          </button>
        </form>

        <div className="flex items-center gap-2 text-[10.5px] text-ink/40">
          <div className="h-px flex-1 bg-ink/10" />
          {t('common.login.or')}
          <div className="h-px flex-1 bg-ink/10" />
        </div>

        <button
          type="button"
          disabled
          title={t('common.login.googleSoonTitle')}
          data-testid="login-google"
          className="flex items-center justify-center gap-2 rounded-md border border-ink/20 bg-white px-3 py-2 text-xs font-semibold text-ink/60 disabled:cursor-not-allowed"
        >
          <span aria-hidden="true">G</span>
          {t('common.login.googleCta')}
          <span className="ms-auto text-[9.5px] font-medium uppercase tracking-wider text-ink/40">
            {t('common.login.googleSoon')}
          </span>
        </button>

        <p className="mb-0 mt-auto text-[10.5px] leading-relaxed text-ink/40">
          {t('common.login.footer')}
        </p>
      </div>
    </div>
  )
}
